import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useUpdateStream from "./useUpdateStream";
import AppConfig from "../AppConfig";
import Constants from "../Constants";

// the PFQ paths are stored relative, and the base url comes from an env var that may carry a trailing slash
const buildPfqUrl = (path) => `${String(AppConfig.PfqBaseUrl).replace(/\/$/, "")}/${path.replace(/^\//, "")}`;

/**
 * Drive a partner case import over the Update Stream: opens an SSE POST and relies on the reader
 * to toast on failure. Classic Intake navigates to the case it just filled; the Easy Mode wizard
 * passes onImported and stays where it is, because the user is mid-flow on page 2.
 * @param {object} params
 * @param {object} params.selectedCase
 * @param {string} params.selectedPartner
 * @param {boolean} params.isAutoImporting - auto-triggers the import once, without a submit click
 * @param {(value: null) => void} params.onClose
 * @param {({ partner: string }) => void} [params.onImported] - handles success in place of navigation
 * @param {string} [params.caseId] - used when the case is not the one in the route
 */
const usePartnerCaseImport = ({
    selectedCase,
    selectedPartner,
    isAutoImporting,
    onClose,
    onImported,
    caseId: caseIdOverride
}) => {
    const [isCaseImporting, setIsCaseImporting] = useState(isAutoImporting);
    const [streamRequest, setStreamRequest] = useState(null);
    const navigate = useNavigate();
    const { caseId: caseIdFromRoute } = useParams();
    const caseId = caseIdOverride ?? caseIdFromRoute;
    const attemptRef = useRef(0);

    const handleUpdate = useCallback((frame) => {
        setIsCaseImporting(false);
        if (frame.success) {
            if (onImported) {
                onImported({ partner: selectedPartner });
            } else {
                navigate(`/case/edit/${caseId}`, { replace: true });
            }
        }
        onClose(null);
    }, [navigate, caseId, onClose, onImported, selectedPartner]);

    useUpdateStream({ request: streamRequest, onUpdate: handleUpdate });

    const startImport = useCallback(() => {
        setIsCaseImporting(true);
        attemptRef.current += 1;
        setStreamRequest({
            // per-attempt nonce so a retry after a failure re-opens the stream
            url: `${buildPfqUrl(Constants.ApiUrl.partner.getCaseById)}?attempt=${attemptRef.current}`,
            body: {
                caseId,
                partner: { id: selectedPartner, caseData: selectedCase }
            }
        });
    }, [caseId, selectedPartner, selectedCase]);

    // fires once when the auto-import modal opens; startImport intentionally excluded to avoid re-firing on its own identity changes
    useEffect(() => {
        if (isAutoImporting) startImport();
    }, [isAutoImporting]);

    return { isCaseImporting, startImport };
};

export default usePartnerCaseImport;
